import { useState } from "react";
import { toast } from "sonner";
import {
  Check, Eye, PlayCircle, RefreshCw, Sparkles, Users, Wand2, Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

export const WritingToolsModal = ({
  open,
  onOpenChange,
  selectedText,
  setSelectedText,
  rewriteResult,
  setRewriteResult,
  rewriteLoading,
  handleRewrite,
  applyRewrite,
}) => {
  return (
      <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setRewriteResult(null); } }}>
        <DialogContent className="sm:max-w-5xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-cyan-400" />
              Writing Polish Tools
            </DialogTitle>
            <DialogDescription>
              Transform flat prose into vivid, immersive writing. Select text in your story first, then choose a technique.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Text Input */}
            <div>
              <Label className="mb-2 block">Text to Transform</Label>
              <Textarea
                value={selectedText}
                onChange={(e) => setSelectedText(e.target.value)}
                rows={4}
                placeholder="Select text from your story or paste it here..."
                className="w-full bg-card border-cyan-500/25 focus:border-cyan-500/35"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {selectedText.length} characters • Tip: Select text in your story before opening this tool
              </p>
            </div>
            
            {/* Interactive Demo Tutorial */}
            {selectedText.length < 10 && (
              <div className="bg-gradient-to-r from-indigo-950/50 to-purple-950/50 border border-indigo-500/25 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-indigo-300 flex items-center gap-2">
                    <PlayCircle className="w-5 h-5" /> Interactive Tutorial
                  </h4>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-900/30"
                    onClick={() => {
                      // Cycle through demo examples
                      const demos = [
                        { text: "She was angry and walked away.", technique: "Show, Don't Tell" },
                        { text: "The room was old and dusty.", technique: "Sensory Expansion" },
                        { text: "He felt sad about losing his job.", technique: "Full Polish" }
                      ];
                      const currentDemo = demos[Math.floor(Math.random() * demos.length)];
                      setSelectedText(currentDemo.text);
                      toast.info(`Try "${currentDemo.technique}" on this example!`);
                    }}
                    data-testid="try-demo-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> Load Example
                  </Button>
                </div>
                
                {/* Before/After Examples */}
                <div className="space-y-3 text-sm">
                  <div className="bg-slate-800/60 rounded-lg p-3 border border-indigo-500/15">
                    <div className="flex items-center gap-2 text-purple-300 font-medium mb-2">
                      <Eye className="w-4 h-4" /> Show, Don't Tell
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-red-950/30 p-2 rounded text-red-300 border border-red-500/15">
                        <span className="text-xs font-medium block mb-1">Before:</span>
                        "She was angry."
                      </div>
                      <div className="bg-green-950/30 p-2 rounded text-green-300 border border-green-500/15">
                        <span className="text-xs font-medium block mb-1">After:</span>
                        "Her jaw clenched, fists white-knuckled at her sides."
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/60 rounded-lg p-3 border border-indigo-500/15">
                    <div className="flex items-center gap-2 text-teal-300 font-medium mb-2">
                      <Sparkles className="w-4 h-4" /> Sensory Expansion
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-red-950/30 p-2 rounded text-red-300 border border-red-500/15">
                        <span className="text-xs font-medium block mb-1">Before:</span>
                        "The room was old."
                      </div>
                      <div className="bg-green-950/30 p-2 rounded text-green-300 border border-green-500/15">
                        <span className="text-xs font-medium block mb-1">After:</span>
                        "Dust motes swirled in pale light, the floorboards groaning beneath each step."
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/60 rounded-lg p-3 border border-indigo-500/15">
                    <div className="flex items-center gap-2 text-rose-300 font-medium mb-2">
                      <Users className="w-4 h-4" /> AI Humanizer
                    </div>
                    <p className="text-slate-400 text-xs">
                      Makes AI-generated text sound more natural and human — helps pass AI detection tools while keeping your meaning.
                    </p>
                  </div>
                </div>
                
                <p className="text-xs text-indigo-400 mt-3 text-center">
                  Click "Load Example" to try it yourself, or paste your own text above!
                </p>
              </div>
            )}
            
            {/* Technique Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleRewrite('show_dont_tell')}
                disabled={rewriteLoading || selectedText.length < 10}
                className={`bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white h-auto py-3 ${selectedText.length < 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="polish-show-dont-tell-btn"
              >
                <div className="text-center">
                  <Eye className="w-5 h-5 mx-auto mb-1" />
                  <span className="block font-semibold">Show, Don't Tell</span>
                  <span className="text-xs opacity-80">Transform telling into showing</span>
                </div>
              </Button>
              
              <Button
                onClick={() => handleRewrite('sensory')}
                disabled={rewriteLoading || selectedText.length < 10}
                className={`bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white h-auto py-3 ${selectedText.length < 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="polish-sensory-btn"
              >
                <div className="text-center">
                  <Sparkles className="w-5 h-5 mx-auto mb-1" />
                  <span className="block font-semibold">Sensory Expansion</span>
                  <span className="text-xs opacity-80">Add sight, sound, smell, touch</span>
                </div>
              </Button>
              
              <Button
                onClick={() => handleRewrite('humanize')}
                disabled={rewriteLoading || selectedText.length < 10}
                className={`bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white h-auto py-3 ${selectedText.length < 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="polish-humanize-btn"
              >
                <div className="text-center">
                  <Users className="w-5 h-5 mx-auto mb-1" />
                  <span className="block font-semibold">AI Humanizer</span>
                  <span className="text-xs opacity-80">Pass AI detection tools</span>
                </div>
              </Button>
              
              <Button
                onClick={() => handleRewrite('polish')}
                disabled={rewriteLoading || selectedText.length < 10}
                className={`bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white h-auto py-3 ${selectedText.length < 10 ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="polish-full-btn"
              >
                <div className="text-center">
                  <Zap className="w-5 h-5 mx-auto mb-1" />
                  <span className="block font-semibold">Full Polish</span>
                  <span className="text-xs opacity-80">All techniques combined</span>
                </div>
              </Button>
            </div>
            
            {/* Loading State */}
            {rewriteLoading && (
              <div className="text-center py-6">
                <RefreshCw className="w-8 h-8 mx-auto animate-spin text-cyan-400" />
                <p className="text-muted-foreground mt-2">Polishing your prose...</p>
              </div>
            )}
            
            {/* Result */}
            {rewriteResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Original */}
                  <div>
                    <Label className="text-red-400 mb-1 block">Original</Label>
                    <div className="p-3 bg-red-950/30 border border-red-500/30 rounded-lg text-sm">
                      {rewriteResult.original}
                    </div>
                  </div>
                  
                  {/* Rewritten */}
                  <div>
                    <Label className="text-green-400 mb-1 block">Polished</Label>
                    <div className="p-3 bg-green-950/30 border border-green-500/30 rounded-lg text-sm">
                      {rewriteResult.rewritten}
                    </div>
                  </div>
                </div>
                
                {/* Apply Button */}
                <Button
                  onClick={applyRewrite}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Apply This Rewrite to My Story
                </Button>
              </div>
            )}
            
            {/* Tips */}
            <div className="bg-slate-800/30 border border-cyan-500/20 rounded-lg p-4">
              <h4 className="font-semibold text-cyan-400 mb-2">Tips for Great Prose</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• <strong>Show, Don't Tell:</strong> Instead of "She was angry," show clenched fists, tight jaw, sharp breaths</li>
                <li>• <strong>Sensory Details:</strong> What do characters see, hear, smell, feel, taste in the scene?</li>
                <li>• <strong>Full Polish:</strong> Use sparingly on key emotional moments for maximum impact</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

  );
};
